import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';
import parseDuration from 'parse-duration';

import { Fields, defaultValues, prepareInput } from 'app/src/elements/forms';
import { Button } from 'app/src/elements/buttons';
import { Loading } from 'app/src/elements/DataState';
import { Title } from 'app/src/styles';
import { useNavigate, useOutletContext } from 'app/src/utils/routing';

import EventThings from './EventThings';
import { CREATE_EVENT, EVENT_SELECTS } from './queries';

export const TIME_TYPES = [
  { value: 'created_at', label: 'Time Info Entered', },
  { value: 'date_only', label: 'Date Only', },
  { value: 'exact', label: 'Exact Time', },
  { value: 'estimated', label: 'Estimated Time', },
];

const DURATION_MESSAGE =
  'Enter duration in a format like 1h 20m or like 20 minutes, 5 seconds';

export const FIELDS = {
  eventTypeId: {
    label: 'Event Type',
    rules: { required: true },
  },
  timeType: {
    label: 'Time Type',
    rules: { required: true },
    select: TIME_TYPES,
  },
  eventAt: {
    label: 'Time',
    type: 'datetime',
    rules: { required: true },
  },
  eventOn: {
    label: 'Date',
    type: 'date',
  },
  clientId: {
    label: 'Client',
    type: 'hidden',
    rules: { required: true },
  },
  notes: {
    label: 'Notes',
  },
  amount: {
    label: 'Amount/Level/Value',
    type: 'hidden',
    blank: 'null',
  },
  duration: {
    label: 'Duration',
    type: 'hidden',
    rules: {
      validate: value => {
        if (value && !parseDuration(value)) {
          return DURATION_MESSAGE;
        }
      }
    }
  },
};

const NewEvent = () => {
  const [eventThings, setEventThings] = useState({});
  const [formStatus, setFormStatus] = useState();

  const navigate = useNavigate();
  const client = useOutletContext()?.client;

  const {
    control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: {
        timeType: 'created_at',
        eventAt: DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm"),
        client,
      },
    }),
  });

  const { data: selects } = useQuery(EVENT_SELECTS);

  const [createEvent] = useMutation(CREATE_EVENT, {
    onCompleted: () => {
      const eventsPath = client ? `/clients/${client.id}/events` : '/events';
      navigate(eventsPath);
    }
  });

  useEffect(() => {
    // Auto select client if only 1 option.
    if (selects && 1 === selects.clients.length) {
      setValue('clientId', selects.clients[0].id);
    }
  }, [selects]);

  const onSubmit = ({ eventOn, ...otherInput }) => {
    setFormStatus('submitting');

    const input = prepareInput(otherInput, FIELDS);

    if ('created_at' === input.timeType) {
      input.eventAt = DateTime.now().toISO();
    } else if ('date_only' === input.timeType) {
      input.eventAt = DateTime.fromISO(`${eventOn}T08:00`).toISO();
    }

    if (otherInput.duration) {
      otherInput.duration = parseDuration(otherInput.duration, 's');
    } else {
      otherInput.duration = null;
    }

    input.eventThingsAttributes = Object.keys(eventThings)
      .map(thingId => eventThings[thingId])
      .filter(eventThing => eventThing.selected)
      .map(eventThing => (
        { thingId: eventThing.thingId, notes: eventThing.notes }
      ));

    createEvent({ variables: { input } });
  };

  const timeType = watch('timeType');
  const eventAt = watch('eventAt');

  const fields = { ...FIELDS };

  if ('date_only' === timeType) {
    if ('datetime' === fields.eventAt.type) {
      setValue('eventOn', eventAt.split('T')[0]);
      fields.eventAt = { ...fields.eventAt, type: 'hidden', rules: { } };
      fields.eventOn = {
        ...fields.eventOn,
        type: 'date',
        rules: { required: true }
      };
    }
  } else if ('created_at' === timeType) {
    fields.eventAt = { ...fields.eventAt, type: 'hidden', rules: { } };
    fields.eventOn = { ...fields.eventOn, type: 'hidden', rules: { } };
  } else {
    fields.eventAt = {
      ...fields.evenAt,
      type: 'datetime',
      rules: { required: true }
    };
    fields.eventOn = { ...fields.eventOn, type: 'hidden', rules: { } };
  }

  if (selects) {
    fields.clientId = { ...fields.clientId };

    if (!client) {
      fields.clientId.type = null;
      fields.clientId.select = [
        { value: '', label: 'Select' },
        ...selects.clients
          .map(entry => ({ value: entry.id, label: entry.name }))
      ];
    }

    fields.eventTypeId = {
      ...fields.eventTypeId,
      type: 'button',
      options: selects.eventTypes,
    };
  }

  const eventType = selects?.eventTypes
    .find(type => type.id === watch('eventTypeId'));

  if (eventType) {
    if (eventType.hasAmount) {
      fields.amount = { ...fields.amount, type: 'Int' };
    }

    if (eventType.amountLabel) {
      fields.amount = { ...fields.amount, label: eventType.amountLabel };
    }

    if (eventType.hasDuration) {
      fields.duration = { ...fields.duration, type: 'string' };
    }
  }

  return (
    <View>
      <Title>Add Event{client && ` for ${client.name}`}</Title>

      <Fields
        fields={fields}
        control={control}
        errors={errors}
      />

      {eventType?.hasThings && (
        <EventThings
          eventThings={eventThings}
          setEventThings={setEventThings}
          watch={watch}
          showButtons
        />
      )}

      {'submitting' === formStatus ? (
        <Loading />
      ) : (
        <Button label="Save" onPress={handleSubmit(onSubmit)} wide />
      )}
    </View>
  );
};

export default NewEvent;
